import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertOctagon, Upload, FileText, Trash2, Download, CheckCircle2, X, AlertTriangle } from "lucide-react";
import { endpoints, API } from "@/lib/api";
import { toast } from "sonner";
import { SendToClientButton } from "@/components/common/SendToClientButton";

const emptyForm = {
  client_slug: "",
  date: new Date().toISOString().slice(0, 10),
  department: "",
  description: "",
  source: "Internal Audit",
  iso_clause: "",
  responsible_person: "",
  root_cause: "",
  corrective_action: "",
  due_date: "",
  status: "Open",
  closure_date: "",
  notes: "",
};

const STATUS_TONE = {
  Open: "bg-blue-50 text-blue-700 border-blue-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  Verification: "bg-purple-50 text-purple-700 border-purple-200",
  Closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Overdue: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function NCRModal({ open, ncrId, defaultClientSlug, clients = [], onOpenChange, onChanged }) {
  const [form, setForm] = useState(emptyForm);
  const [loaded, setLoaded] = useState(null);
  const [sources, setSources] = useState({ sources: ["Internal Audit"], statuses: ["Open"] });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState({ name: "" });
  const fileRef = useRef(null);
  const isEdit = !!ncrId;

  useEffect(() => {
    if (open) {
      endpoints.ncrSources().then(setSources).catch(() => {});
      endpoints.me().then(setMe).catch(() => {});
      if (isEdit) {
        endpoints.getNcr(ncrId).then((n) => {
          setLoaded(n);
          setForm({ ...emptyForm, ...n });
        });
      } else {
        setLoaded(null);
        setForm({ ...emptyForm, client_slug: defaultClientSlug || clients[0]?.slug || "" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ncrId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const overdue = loaded?.status === "Overdue";

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.description.trim()) return toast.error("Description is required");
    if (!form.client_slug) return toast.error("Select a client");
    setSaving(true);
    try {
      if (isEdit) {
        const { client_slug: _cs, ...rest } = form;
        await endpoints.updateNcr(ncrId, rest);
        toast.success("NCR updated");
      } else {
        const created = await endpoints.createNcr(form);
        toast.success(`Created ${created.ncr_number}`);
      }
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save NCR");
    } finally { setSaving(false); }
  };

  const closeNcr = async () => {
    if (!isEdit) return;
    setSaving(true);
    try {
      const updated = await endpoints.updateNcr(ncrId, { status: "Closed" });
      setLoaded(updated); setForm({ ...emptyForm, ...updated });
      toast.success("NCR closed");
      onChanged?.();
    } catch { toast.error("Could not close NCR"); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!isEdit || !window.confirm(`Delete ${loaded?.ncr_number}? This cannot be undone.`)) return;
    setSaving(true);
    try { await endpoints.deleteNcr(ncrId); toast.success("NCR deleted"); onChanged?.(); }
    catch { toast.error("Could not delete NCR"); } finally { setSaving(false); }
  };

  const uploadFile = async (file) => {
    if (!isEdit) { toast.error("Save the NCR first before adding evidence"); return; }
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large — max 5 MB"); return; }
    setUploading(true);
    try {
      await endpoints.uploadNcrEvidence(ncrId, file, me.name);
      const updated = await endpoints.getNcr(ncrId);
      setLoaded(updated);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteEvidence = async (evId) => {
    if (!window.confirm("Remove this evidence file?")) return;
    try {
      await endpoints.deleteNcrEvidence(ncrId, evId);
      const updated = await endpoints.getNcr(ncrId);
      setLoaded(updated);
    } catch { toast.error("Could not delete file"); }
  };

  const evidence = loaded?.evidence || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-ncr" className="sm:max-w-[820px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center shadow-md shadow-orange-500/25">
              <AlertOctagon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {isEdit ? (
                  <>
                    <span className="font-mono">{loaded?.ncr_number || "…"}</span>
                    {loaded && <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[loaded.status] || ""}`}>{loaded.status}</Badge>}
                    {overdue && <span className="text-rose-500 text-xs font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> overdue</span>}
                  </>
                ) : "New Non-Conformity Report"}
              </DialogTitle>
              <DialogDescription>
                {isEdit ? `${loaded?.client_name || ""} · raised ${loaded?.date || ""}` : "Capture a non-conformity and its corrective action."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4 pt-2">
          <section>
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="n-desc">Description of non-conformity</Label>
                <Textarea id="n-desc" data-testid="ncr-desc-input" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe what went wrong..." />
              </div>
              <div>
                <Label>Client</Label>
                <Select value={form.client_slug} onValueChange={(v) => set("client_slug", v)} disabled={isEdit}>
                  <SelectTrigger data-testid="ncr-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="n-date">Date raised</Label>
                <Input id="n-date" data-testid="ncr-date-input" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="n-dept">Department</Label>
                <Input id="n-dept" data-testid="ncr-dept-input" value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="e.g. Warehouse" />
              </div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="ncr-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{sources.sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="n-clause">ISO clause</Label>
                <Input id="n-clause" data-testid="ncr-clause-input" value={form.iso_clause} onChange={(e) => set("iso_clause", e.target.value)} placeholder="e.g. 8.6" />
              </div>
              <div>
                <Label htmlFor="n-owner">Responsible person</Label>
                <Input id="n-owner" data-testid="ncr-owner-input" value={form.responsible_person} onChange={(e) => set("responsible_person", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="n-due">Due date</Label>
                <Input id="n-due" data-testid="ncr-due-input" type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status === "Overdue" ? "In Progress" : form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="ncr-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{sources.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.status === "Closed" && (
                <div>
                  <Label htmlFor="n-closure">Closure date</Label>
                  <Input id="n-closure" data-testid="ncr-closure-input" type="date" value={form.closure_date || ""} onChange={(e) => set("closure_date", e.target.value)} />
                </div>
              )}
            </div>
          </section>

          <section>
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Analysis & action</h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="n-root">Root cause</Label>
                <Textarea id="n-root" data-testid="ncr-root-input" rows={2} value={form.root_cause} onChange={(e) => set("root_cause", e.target.value)} placeholder="Why did this happen?" />
              </div>
              <div>
                <Label htmlFor="n-ca">Corrective action</Label>
                <Textarea id="n-ca" data-testid="ncr-ca-input" rows={2} value={form.corrective_action} onChange={(e) => set("corrective_action", e.target.value)} placeholder="What will be done to fix and prevent recurrence?" />
              </div>
              <div>
                <Label htmlFor="n-notes">Notes</Label>
                <Textarea id="n-notes" data-testid="ncr-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              </div>
            </div>
          </section>

          {isEdit && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Supporting evidence ({evidence.length})</h3>
                <div className="flex gap-2">
                  <input ref={fileRef} data-testid="ncr-evidence-input" type="file" hidden onChange={(e) => uploadFile(e.target.files?.[0])} />
                  <Button data-testid="ncr-evidence-upload-button" type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    <Upload className="h-3.5 w-3.5 mr-1" /> {uploading ? "Uploading..." : "Upload evidence"}
                  </Button>
                </div>
              </div>
              {evidence.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 dark:border-white/10 p-6 text-center text-xs text-slate-500">
                  Drag files or click Upload to attach supporting evidence (5 MB max per file).
                </div>
              ) : (
                <div className="space-y-2">
                  {evidence.map((e) => (
                    <div key={e.id} data-testid={`ncr-evidence-${e.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-2.5">
                      <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 flex items-center justify-center shrink-0"><FileText className="h-4 w-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate">{e.filename}</div>
                        <div className="text-[10px] text-slate-500">{(e.size / 1024).toFixed(1)} KB · {e.uploaded_by} · {new Date(e.uploaded_at).toLocaleDateString()}</div>
                      </div>
                      <Button asChild size="icon" variant="ghost" className="h-7 w-7"><a href={`${API}/ncrs/${ncrId}/evidence/${e.id}`} download><Download className="h-3.5 w-3.5" /></a></Button>
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => deleteEvidence(e.id)}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {isEdit && (
              <>
                <Button data-testid="ncr-delete-button" type="button" variant="ghost" className="text-rose-600 mr-auto" onClick={remove} disabled={saving}>
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
                <Button asChild variant="outline" size="sm" data-testid="ncr-export-pdf-button">
                  <a href={endpoints.exportNcrPdfUrl(ncrId)} download><Download className="h-3.5 w-3.5 mr-1" /> Export PDF</a>
                </Button>
                <SendToClientButton kind="ncr" refId={ncrId} />
                {form.status !== "Closed" && (
                  <Button data-testid="ncr-close-button" type="button" variant="outline" onClick={closeNcr} disabled={saving}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Close NCR
                  </Button>
                )}
              </>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
            <Button data-testid="ncr-submit-button" type="submit" className="brand-gradient text-white" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save changes" : "Create NCR"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
